import type {
  AuditIntroducerSettlementParams,
  BatchBusinessApproveIntroducerSettlementParams,
  CreateIntroducerParams,
  CreateIntroducerRelationParams,
  DeleteIntroducerAccountTypeRateParams,
  DeleteIntroducerCustomerRateRangeParams,
  DeleteIntroducerCustomerRateRuleParams,
  IntroducerAccountTypeRateItem,
  IntroducerBaseRebateSegmentItem,
  IntroducerCustomerRateRangeItem,
  IntroducerCustomerRateRuleItem,
  IntroducerEffectivePreviewResponse,
  IntroducerListItem,
  IntroducerProfileCandidateItem,
  IntroducerRelationItem,
  IntroducerSettlementBatchItem,
  IntroducerSettlementItem,
  IntroducerSpecialRebateAccountItem,
  ManualTriggerIntroducerSettlementParams,
  ManualTriggerIntroducerSettlementResponse,
  QueryIntroducerAccountTypeRateParams,
  QueryIntroducerCustomerRateRangeParams,
  QueryIntroducerCustomerRateRuleParams,
  QueryIntroducerEffectivePreviewParams,
  QueryIntroducerListCondition,
  QueryIntroducerProfileCandidateCondition,
  QueryIntroducerRelationCondition,
  QueryIntroducerSettlementCondition,
  QueryIntroducerSettlementDetailParams,
  QueryIntroducerSettlementDetailResponse,
  QueryIntroducerSpecialRebateCondition,
  SaveIntroducerAccountTypeRateParams,
  SaveIntroducerCustomerRateRangeParams,
  SaveIntroducerCustomerRateRuleParams,
  UpdateIntroducerProfileParams,
  UpsertIntroducerBaseRebateSegmentParams,
  UpsertIntroducerSpecialRebateSegmentParams,
} from './type/introducer'

import type { PagingRequest, PagingResponse } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { EXPORT_TASK_BIZ_CODE, exportTaskApi } from '#/api/modules/common'
import { requestClient } from '#/api/request'
import { dayjs, formatDateRange } from '#/utils'

export const introducerApi = defineApi('operation', ({ prefix }) => ({
  async queryIntroducerList(
    params: PagingRequest<QueryIntroducerListCondition>,
  ) {
    return requestClient
      .post<PagingResponse<IntroducerListItem>>(
        prefix('adsIntroducerRebate/queryProfile'),
        {
          condition: {
            becameIntroducerAtRange: formatDateRange(
              params.condition?.becameIntroducerAtRange,
            ),
            clientConsumeVisible: params.condition?.clientConsumeVisible,
            createdByList: params.condition?.createdByList,
            customerIds: params.condition?.customerIds,
            customerName: params.condition?.customerName,
            status: params.condition?.status,
          },
          orderBy: params.orderBy ?? 'DESC',
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          sortBy: params.sortBy ?? 'becameIntroducerAt',
        },
      )
      .then((res) => {
        return {
          list: (res.list ?? []).map((item) => ({
            becameIntroducerAt: item.becameIntroducerAt,
            clientConsumeVisible: item.clientConsumeVisible,
            createdBy: item.createdBy,
            createdTime: item.createdTime,
            customerId: item.customerId,
            customerName: item.customerName,
            id: item.id,
            rateRuleCount: item.rateRuleCount,
            relatedCustomerCount: item.relatedCustomerCount,
            status: item.status,
            updatedBy: item.updatedBy,
            updatedTime: item.updatedTime,
          })),
          page: res.page,
          pageSize: res.pageSize,
          total: res.total,
        }
      })
  },

  async exportIntroducerList(condition: QueryIntroducerListCondition) {
    return exportTaskApi.submitExportTask(
      EXPORT_TASK_BIZ_CODE.ADS_INTRODUCER_PROFILE,
      {
        condition: {
          becameIntroducerAtRange: formatDateRange(
            condition.becameIntroducerAtRange,
          ),
          clientConsumeVisible: condition.clientConsumeVisible,
          createdByList: condition.createdByList,
          customerIds: condition.customerIds,
          customerName: condition.customerName,
          status: condition.status,
        },
        orderBy: 'DESC',
        sortBy: 'becameIntroducerAt',
      },
    )
  },

  async queryIntroducerProfileCandidates(
    params: PagingRequest<QueryIntroducerProfileCandidateCondition>,
  ) {
    const customerId = String(params.condition?.customerId || '').trim()

    return requestClient
      .post<PagingResponse<IntroducerProfileCandidateItem>>(
        prefix('adsIntroducerRebate/queryProfileCandidates'),
        {
          condition: {
            customerId: customerId ? Number(customerId) : undefined,
            customerName: params.condition?.customerName,
          },
          orderBy: params.orderBy,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
        },
      )
      .then((res) => {
        return {
          list: (res.list ?? []).map((item) => ({
            customerId: item.customerId,
            customerName: item.customerName,
          })),
          page: res.page,
          pageSize: res.pageSize,
          total: res.total,
        }
      })
  },

  async createIntroducer(params: CreateIntroducerParams) {
    return requestClient.post(prefix('adsIntroducerRebate/createProfile'), {
      customerId: params.customerId,
    })
  },

  async queryIntroducerAccountTypeRates(
    params: QueryIntroducerAccountTypeRateParams,
  ) {
    const requestParams = {
      introducerCustomerId: params.introducerCustomerId,
    }

    return requestClient
      .post<{
        list?: IntroducerAccountTypeRateItem[]
      }>(prefix('adsIntroducerRebate/listAccountTypeRate'), requestParams)
      .then((res) =>
        (res.list ?? []).map((item) => ({
          accountType: item.accountType,
          configStatus: item.configStatus,
          endDate: item.endDate,
          id: item.id,
          mediaPlatform: item.mediaPlatform,
          ratePercent: item.ratePercent,
          startDate: item.startDate,
          updatedBy: item.updatedBy,
          updatedTime: item.updatedTime,
        })),
      )
  },

  async saveIntroducerAccountTypeRate(
    params: SaveIntroducerAccountTypeRateParams,
  ) {
    return requestClient.post(
      prefix('adsIntroducerRebate/saveAccountTypeRate'),
      {
        accountType: params.accountType,
        configStatus: params.configStatus,
        endDate: params.endDate,
        id: params.id,
        introducerCustomerId: params.introducerCustomerId,
        mediaPlatform: params.mediaPlatform,
        ratePercent: params.ratePercent,
        startDate: params.startDate,
      },
    )
  },

  async deleteIntroducerAccountTypeRate(
    params: DeleteIntroducerAccountTypeRateParams,
  ) {
    return requestClient.post(
      prefix('adsIntroducerRebate/deleteAccountTypeRate'),
      {
        id: params.id,
      },
    )
  },

  async queryIntroducerCustomerRateRanges(
    params: QueryIntroducerCustomerRateRangeParams,
  ) {
    const requestParams = {
      relationId: params.relationId,
    }

    return requestClient
      .post<{
        list?: IntroducerCustomerRateRangeItem[]
      }>(prefix('adsIntroducerRebate/listCustomerRateRange'), requestParams)
      .then((res) =>
        (res.list ?? []).map((item) => ({
          configStatus: item.configStatus,
          endDate: item.endDate,
          id: item.id,
          relationId: item.relationId,
          ruleCount: item.ruleCount,
          startDate: item.startDate,
          updatedBy: item.updatedBy,
          updatedTime: item.updatedTime,
        })),
      )
  },

  async saveIntroducerCustomerRateRange(
    params: SaveIntroducerCustomerRateRangeParams,
  ) {
    return requestClient.post(
      prefix('adsIntroducerRebate/saveCustomerRateRange'),
      {
        configStatus: params.configStatus,
        endDate: params.endDate,
        id: params.id,
        relationId: params.relationId,
        startDate: params.startDate,
      },
    )
  },

  async deleteIntroducerCustomerRateRange(
    params: DeleteIntroducerCustomerRateRangeParams,
  ) {
    return requestClient.post(
      prefix('adsIntroducerRebate/deleteCustomerRateRange'),
      {
        id: params.id,
      },
    )
  },

  async queryIntroducerCustomerRateRules(
    params: QueryIntroducerCustomerRateRuleParams,
  ) {
    const requestParams = {
      rangeId: params.rangeId,
    }

    return requestClient
      .post<{
        list?: IntroducerCustomerRateRuleItem[]
      }>(prefix('adsIntroducerRebate/listCustomerRateRule'), requestParams)
      .then((res) =>
        (res.list ?? []).map((item) => ({
          accountType: item.accountType,
          configStatus: item.configStatus,
          id: item.id,
          mediaPlatform: item.mediaPlatform,
          rangeId: item.rangeId,
          ratePercent: item.ratePercent,
          updatedBy: item.updatedBy,
          updatedTime: item.updatedTime,
        })),
      )
  },

  async saveIntroducerCustomerRateRule(
    params: SaveIntroducerCustomerRateRuleParams,
  ) {
    return requestClient.post(
      prefix('adsIntroducerRebate/saveCustomerRateRule'),
      {
        accountType: params.accountType,
        configStatus: params.configStatus,
        id: params.id,
        mediaPlatform: params.mediaPlatform,
        rangeId: params.rangeId,
        ratePercent: params.ratePercent,
      },
    )
  },

  async deleteIntroducerCustomerRateRule(
    params: DeleteIntroducerCustomerRateRuleParams,
  ) {
    return requestClient.post(
      prefix('adsIntroducerRebate/deleteCustomerRateRule'),
      {
        id: params.id,
      },
    )
  },

  async createIntroducerRelation(params: CreateIntroducerRelationParams) {
    return requestClient.post(prefix('adsIntroducerRebate/createRelation'), {
      baseRate: params.baseRate
        ? {
            effectiveEndDate: params.baseRate.effectiveEndDate,
            effectiveStartDate: params.baseRate.effectiveStartDate,
            ratePercent: params.baseRate.ratePercent,
          }
        : undefined,
      bindEndDate: params.bindEndDate,
      bindStartDate: params.bindStartDate,
      introducerCustomerId: params.introducerCustomerId,
      refereeCustomerId: params.refereeCustomerId,
    })
  },

  async queryIntroducerRelationList(
    params: PagingRequest<QueryIntroducerRelationCondition>,
  ) {
    return requestClient
      .post<PagingResponse<IntroducerRelationItem>>(
        prefix('adsIntroducerRebate/queryRelation'),
        {
          condition: {
            currentStatus: params.condition?.currentStatus,
            introducerCustomerIds: params.condition?.introducerCustomerIds,
            introducerCustomerName: params.condition?.introducerCustomerName,
            refereeCustomerIds: params.condition?.refereeCustomerIds,
            refereeCustomerName: params.condition?.refereeCustomerName,
            ruleType: params.condition?.ruleType,
          },
          orderBy: params.orderBy,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
        },
      )
      .then((res) => {
        return {
          list: (res.list ?? []).map((item) => ({
            bindEndDate: item.bindEndDate,
            bindStartDate: item.bindStartDate,
            currentStatus: item.currentStatus,
            id: item.id,
            introducerCustomerId: item.introducerCustomerId,
            introducerCustomerName: item.introducerCustomerName,
            refereeCustomerId: item.refereeCustomerId,
            refereeCustomerName: item.refereeCustomerName,
            relationStatus: item.relationStatus,
            ruleType: item.ruleType,
            ruleTypes: item.ruleTypes,
            specialRateEnabledCount: item.specialRateEnabledCount,
            updatedTime: item.updatedTime,
          })),
          page: res.page,
          pageSize: res.pageSize,
          total: res.total,
        }
      })
  },

  async queryIntroducerBaseRebateSegments(relationId: number) {
    return requestClient
      .post<IntroducerBaseRebateSegmentItem[]>(
        prefix('adsIntroducerRebate/listBaseRate'),
        {
          relationId,
        },
      )
      .then((res) =>
        (res ?? []).map((item) => ({
          effectiveEndDate: item.effectiveEndDate,
          effectiveStartDate: item.effectiveStartDate,
          id: item.id,
          ratePercent: item.ratePercent,
          relationId: item.relationId,
          updatedBy: item.updatedBy,
          updatedTime: item.updatedTime,
        })),
      )
  },

  async upsertIntroducerBaseRebateSegment(
    params: UpsertIntroducerBaseRebateSegmentParams,
  ) {
    return requestClient.post(prefix('adsIntroducerRebate/saveBaseRate'), {
      effectiveEndDate: params.effectiveEndDate,
      effectiveStartDate: params.effectiveStartDate,
      id: params.id,
      ratePercent: params.ratePercent,
      relationId: params.relationId,
    })
  },

  async deleteIntroducerBaseRebateSegment(id: number) {
    return requestClient.post(prefix('adsIntroducerRebate/deleteBaseRate'), {
      id,
    })
  },

  async queryIntroducerSpecialRebateAccounts(
    params: PagingRequest<QueryIntroducerSpecialRebateCondition>,
  ) {
    return requestClient
      .post<PagingResponse<IntroducerSpecialRebateAccountItem>>(
        prefix('adsIntroducerRebate/listSpecialRate'),
        {
          condition: {
            accountId: params.condition?.accountId,
            accountName: params.condition?.accountName,
            relationId: params.condition?.relationId,
          },
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
        },
      )
      .then((res) => {
        return {
          list: (res.list ?? []).map((item) => ({
            accountId: item.accountId,
            accountName: item.accountName,
            bindingSource: item.bindingSource,
            mediaPlatform: item.mediaPlatform,
            relationId: item.relationId,
            segments: item.segments.map((segment) => ({
              configStatus: segment.configStatus,
              effectiveEndDate: segment.effectiveEndDate,
              effectiveStartDate: segment.effectiveStartDate,
              id: segment.id,
              ratePercent: segment.ratePercent,
              updatedBy: segment.updatedBy,
              updatedTime: segment.updatedTime,
            })),
          })),
          page: res.page,
          pageSize: res.pageSize,
          total: res.total,
        }
      })
  },

  async upsertIntroducerSpecialRebateSegment(
    params: UpsertIntroducerSpecialRebateSegmentParams,
  ) {
    return requestClient.post(prefix('adsIntroducerRebate/saveSpecialRate'), {
      accountId: params.accountId,
      configStatus: params.configStatus,
      effectiveEndDate: params.effectiveEndDate
        ? dayjs(params.effectiveEndDate).format('YYYY-MM-DD')
        : undefined,
      effectiveStartDate: dayjs(params.effectiveStartDate).format('YYYY-MM-DD'),
      id: params.id,
      mediaPlatform: params.mediaPlatform,
      ratePercent: params.ratePercent,
      relationId: params.relationId,
    })
  },

  async queryIntroducerEffectivePreview(
    params: QueryIntroducerEffectivePreviewParams,
  ): Promise<IntroducerEffectivePreviewResponse> {
    return requestClient
      .post<IntroducerEffectivePreviewResponse>(
        prefix('adsIntroducerRebate/queryEffectivePreview'),
        {
          quarter: params.quarter,
          relationId: params.relationId,
          year: params.year,
        },
      )
      .then((res) => {
        return {
          accountTracks: (res.accountTracks ?? []).map((track) => ({
            accountId: track.accountId,
            accountName: track.accountName,
            mediaPlatform: track.mediaPlatform,
            segments: (track.segments ?? []).map((segment) => ({
              endDate: segment.endDate,
              ratePercent: segment.ratePercent,
              rateSource: segment.rateSource,
              startDate: segment.startDate,
            })),
          })),
          quarter: res.quarter,
          quarterEndDate: res.quarterEndDate,
          quarterStartDate: res.quarterStartDate,
          year: res.year,
        }
      })
  },

  async queryIntroducerSettlementList(
    params: PagingRequest<QueryIntroducerSettlementCondition>,
  ) {
    return requestClient
      .post<PagingResponse<IntroducerSettlementItem>>(
        prefix('adsIntroducerRebate/querySettlementOrder'),
        {
          condition: {
            batchCode: params.condition?.batchCode,
            introducerCustomerIds: params.condition?.introducerCustomerIds,
            introducerCustomerName: params.condition?.introducerCustomerName,
            states: params.condition?.states,
          },
          orderBy: params.orderBy,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
        },
      )
      .then((res) => {
        return {
          list: (res.list ?? []).map((item) => ({
            batchCode: item.batchCode,
            bizApprovedAt: item.bizApprovedAt,
            bizApprover: item.bizApprover,
            details: item.details,
            financeApprovedAt: item.financeApprovedAt,
            financeApprover: item.financeApprover,
            id: item.id,
            introducerCustomerId: item.introducerCustomerId,
            introducerCustomerName: item.introducerCustomerName,
            involvedAccountCount: item.involvedAccountCount,
            involvedCustomerCount: item.involvedCustomerCount,
            orderNo: item.orderNo,
            remark: item.remark,
            state: item.state,
            stateLabel: item.stateLabel,
            totalOurCompanyProfit: item.totalOurCompanyProfit,
            totalRebate: item.totalRebate,
            totalSpend: item.totalSpend,
            triggerType: item.triggerType,
            updatedTime: item.updatedTime,
          })),
          page: res.page,
          pageSize: res.pageSize,
          total: res.total,
        }
      })
  },

  async exportIntroducerSettlementList(
    condition: QueryIntroducerSettlementCondition,
  ) {
    return exportTaskApi.submitExportTask(
      EXPORT_TASK_BIZ_CODE.ADS_INTRODUCER_SETTLEMENT_ORDER,
      {
        condition: {
          batchCode: condition.batchCode,
          introducerCustomerIds: condition.introducerCustomerIds,
          introducerCustomerName: condition.introducerCustomerName,
          states: condition.states,
        },
      },
    )
  },

  async getIntroducerSettlementDetail(
    params: QueryIntroducerSettlementDetailParams,
  ) {
    return requestClient
      .post<QueryIntroducerSettlementDetailResponse>(
        prefix('adsIntroducerRebate/getSettlementOrderDetail'),
        {
          orderBy: params.orderBy,
          orderId: params.orderId,
          sortBy: params.sortBy,
        },
      )
      .then((res) => {
        return {
          lines: (res.lines ?? []).map((item) => ({
            accountId: item.accountId,
            accountName: item.accountName,
            mediaPlatform: item.mediaPlatform,
            ourCompanyProfit: item.ourCompanyProfit,
            ratePercent: item.ratePercent,
            rateSource: item.rateSource,
            rebate: item.rebate,
            refereeCustomerId: item.refereeCustomerId,
            refereeCustomerName: item.refereeCustomerName,
            segmentEndDate: item.segmentEndDate,
            segmentStartDate: item.segmentStartDate,
            spend: item.spend,
          })),
          order: {
            batchCode: res.order.batchCode,
            bizApprovedAt: res.order.bizApprovedAt,
            bizApprover: res.order.bizApprover,
            details: res.order.details,
            financeApprovedAt: res.order.financeApprovedAt,
            financeApprover: res.order.financeApprover,
            id: res.order.id,
            introducerCustomerId: res.order.introducerCustomerId,
            introducerCustomerName: res.order.introducerCustomerName,
            involvedAccountCount: res.order.involvedAccountCount,
            involvedCustomerCount: res.order.involvedCustomerCount,
            orderNo: res.order.orderNo,
            remark: res.order.remark,
            state: res.order.state,
            stateLabel: res.order.stateLabel,
            totalOurCompanyProfit: res.order.totalOurCompanyProfit,
            totalRebate: res.order.totalRebate,
            totalSpend: res.order.totalSpend,
            triggerType: res.order.triggerType,
            updatedTime: res.order.updatedTime,
          },
        }
      })
  },

  async batchBusinessApproveSettlement(
    params: BatchBusinessApproveIntroducerSettlementParams,
  ) {
    return requestClient.post(
      prefix('adsIntroducerRebate/batchBizApproveSettlement'),
      {
        orderIds: params.orderIds,
        remark: params.remark,
      },
    )
  },

  async approveIntroducerSettlement(params: AuditIntroducerSettlementParams) {
    return requestClient.post(prefix('adsIntroducerRebate/approveSettlement'), {
      action: params.action,
      orderId: params.orderId,
      remark: params.remark,
      step: params.step,
    })
  },

  async queryIntroducerSettlementBatchList(): Promise<
    IntroducerSettlementBatchItem[]
  > {
    return requestClient
      .post<
        IntroducerSettlementBatchItem[]
      >(prefix('adsIntroducerRebate/listSettlementBatch'), {})
      .then((res) =>
        (res ?? []).map((item) => ({
          batchCode: item.batchCode,
          id: item.id,
          quarterEndDate: item.quarterEndDate,
          quarterStartDate: item.quarterStartDate,
          remark: item.remark,
          triggerType: item.triggerType,
        })),
      )
  },

  async manualTriggerSettlement(
    params: ManualTriggerIntroducerSettlementParams,
  ) {
    return requestClient
      .post<ManualTriggerIntroducerSettlementResponse>(
        prefix('adsIntroducerRebate/triggerManualSettlement'),
        {
          batchId: params.batchId,
          introducerCustomerIds: params.introducerCustomerIds,
        },
      )
      .then((res) => ({
        orderIds: res.orderIds,
        skipped: res.skipped,
        successCount: res.successCount,
      }))
  },

  async updateIntroducerProfile(params: UpdateIntroducerProfileParams) {
    return requestClient.post(prefix('adsIntroducerRebate/updateProfile'), {
      clientConsumeVisible: params.clientConsumeVisible,
      id: params.id,
      status: params.status,
    })
  },
}))
